import type { AnimatedValues } from "@excalidraw/element";
import type { ExcalidrawElement } from "@excalidraw/element/types";

import type { gsap } from "gsap";

import type { ElementAnimation } from "../types";

export type ParamSchema = {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  /** enum params: option labels indexed by value (renders as a select) */
  options?: readonly string[];
};

export type AnimationCategory =
  | "custom"
  | "attention"
  | "micro"
  | "entrance"
  | "exit"
  | "text"
  | "morph"
  | "threeD"
  | "color"
  | "physics"
  | "spatial"
  | "draw"
  | "templates"
  | "flipbook";

export const CATEGORY_ORDER: AnimationCategory[] = [
  "custom",
  "templates",
  "attention",
  "micro",
  "entrance",
  "exit",
  "text",
  "morph",
  "threeD",
  "color",
  "physics",
  "spatial",
  "draw",
];

export const CATEGORY_LABELS: Record<AnimationCategory, string> = {
  custom: "Custom",
  templates: "Templates",
  attention: "Attention",
  micro: "Micro",
  entrance: "Entrance",
  exit: "Exit",
  text: "Text",
  morph: "Morph",
  threeD: "3D & Projection",
  color: "Color",
  physics: "Physics",
  spatial: "Spatial & Flow",
  draw: "Draw",
  flipbook: "Flipbook",
};

/** Muted per-category tint for the template card preview wash — kept subtle
 * so it reads as a hint of identity, not a competing accent to --apple-accent. */
export const CATEGORY_ACCENTS: Record<AnimationCategory, string> = {
  custom: "#8e8e93",
  templates: "#5ac8b8",
  attention: "#e0839f",
  micro: "#bfa07a",
  threeD: "#7a8fd6",
  entrance: "#6fbf73",
  exit: "#d98a5f",
  text: "#8a7fd6",
  morph: "#5a9bd6",
  color: "#d6b45a",
  physics: "#c77dd6",
  spatial: "#5ab0d6",
  draw: "#a3a35a",
  flipbook: "#5ac8b8",
};

export type AnimationPreset = {
  id: string;
  name: string;
  category: AnimationCategory;
  defaults: Omit<ElementAnimation, "preset">;
  paramsSchema: ParamSchema[];
  /** Adds tweens for one element onto the shared timeline at position 0.
   * Mutates `target` (a plain object), never the element. `sceneElements`
   * (when provided by the runtime) lets presets reference other elements,
   * e.g. followPath resolving its motion path. */
  build: (
    tl: gsap.core.Timeline,
    target: AnimatedValues,
    element: ExcalidrawElement,
    cfg: ElementAnimation,
    sceneElements?: readonly ExcalidrawElement[],
  ) => void;
};

/** Every entry must resolve via gsap.parseEase — the named wiggle/bounce
 * eases are created in helpers.ts at module load (plugin registration lives
 * there too). presets.test.ts sweeps this list. */
// the full GSAP built-in matrix (every family × in/out/inOut — also covers
// anime.js's quad..quint/expo/circ/sine equivalents), then the stepped,
// plugin-backed and custom-created eases
export const EASING_OPTIONS = [
  "none",
  "power1.in",
  "power1.out",
  "power1.inOut",
  "power2.in",
  "power2.out",
  "power2.inOut",
  "power3.in",
  "power3.out",
  "power3.inOut",
  "power4.in",
  "power4.out",
  "power4.inOut",
  "sine.in",
  "sine.out",
  "sine.inOut",
  "expo.in",
  "expo.out",
  "expo.inOut",
  "circ.in",
  "circ.out",
  "circ.inOut",
  "back.in",
  "back.out",
  "back.inOut",
  "elastic.in",
  "elastic.out",
  "elastic.inOut",
  "bounce.in",
  "bounce.out",
  "bounce.inOut",
  "steps(4)",
  "steps(8)",
  "steps(12)",
  "rough",
  "rough({ points: 30, strength: 2 })",
  "slow(0.7, 0.7)",
  "wiggle6",
  "wiggle12",
  "bounceSoft",
] as const;

/**
 * Categories that exist for the runtime but must never appear in the picker
 * — their presets are generated, not hand-applied. Excluded from
 * CATEGORY_ORDER (which drives the grids) and from search.
 */
export const HIDDEN_CATEGORIES: ReadonlySet<AnimationCategory> = new Set([
  "flipbook",
]);
